<template>
    <div>
        <img class="brand-modal-mark" :class="riseClass" src="/img/ScriptlyLogo.png" alt="">
        <p class="brand-modal-eyebrow" :class="[riseClass, { 'brand-modal-eyebrow--pill': sandboxMode }]"
            :style="riseDelay(90)">
            {{ eyebrow }}
        </p>
        <h2 class="brand-modal-title" :class="riseClass" :style="riseDelay(160)">{{ title }}</h2>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { BRAND_MODAL_RISE, brandModalRiseDelay } from "@/components/Modals/brandModalHeader";

/**
 * The brand band shared by the welcome message and the guided tour hub: the wordmark, a line
 * saying where you are, and the modal's own title.
 *
 * Goes in a modal's `modal-title` slot, not its `modal-header` slot. Bootstrap Vue wires the
 * dialog's `aria-labelledby` to the title element's id and takes no account of a header slot, so
 * replacing the header outright leaves the dialog pointing at an element that is no longer
 * rendered -- worse than no name at all. Using the title slot keeps that wiring, and the close
 * button, exactly as they were; the host pairs it with `brandModalHeaderClass` so the band paints.
 *
 * Purely presentational: it owns no state, and the host decides when the entrance runs.
 */
@Component
export default class BrandModalHeader extends Vue {
    // `type:` matters on both booleans: without emitDecoratorMetadata the TypeScript type never
    // reaches Vue, and an untyped prop is not cast, so a valueless attribute would arrive as "".
    /** Trial tenant: a teal band, and the eyebrow becomes a status pill. */
    @Prop({ type: Boolean, default: false }) sandboxMode!: boolean;

    /** The modal's own heading, and the dialog's accessible name. */
    @Prop({ type: String, required: true }) title!: string;

    /**
     * True from the host's `show` handler onwards. The host owns this rather than the band
     * animating on mount, because a modal's contents are mounted long before anybody opens it --
     * see `BRAND_MODAL_RISE` for why the animation never owns the resting state.
     */
    @Prop({ type: Boolean, default: false }) animate!: boolean;

    get riseClass(): string {
        return this.animate ? BRAND_MODAL_RISE : "";
    }

    /** Where you are, in three or four words. On a trial that is a status, so it reads as one. */
    get eyebrow(): string {
        return this.sandboxMode ? "Trial environment" : "Your pharmacy workspace";
    }

    protected riseDelay(ms: number): Record<string, string> {
        return brandModalRiseDelay(ms);
    }
}
</script>
